import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { formatUSDEnsure2Digits } from '../../common/currency';
import { useGetTokenOHLCVQuery } from '../../redux/api/public/coingecko';
/**
 * TokenChart Component
 *
 * Displays OHLCV (Open, High, Low, Close, Volume) price data for tokens using CoinGecko API
 * Includes interactive timeframe selector buttons: 1H, 1D (default), 1W, 1M, YTD, ALL
 *
 * @param {string} tokenAddress - The token contract address
 * @param {string} height - Chart container height (default: '400px')
 * @param {boolean} showStats - Show statistics panel (default: true)
 * @param {string} network - Blockchain network (default: 'solana')
 *
 * The component uses a combination of timeframe and aggregate parameters:
 * - timeframe: 'day', 'hour', 'minute', 'second'
 * - aggregate: time period to aggregate each OHLCV (varies by timeframe)
 *
 * Environment Variables:
 *   - VITE_COINGECKO_API_KEY: CoinGecko Pro API key
 */

const ChartContainer = styled.div`
  width: 100%;
  height: fit-content;
  min-height: ${(props) => props.$height || '400px'};
  background: ${({ theme }) => theme.background.primary};
  border-radius: 12px;
  padding-top: 20px;
`;
const ChartWrapper = styled.div`
  width: 100%;
  height: calc(100% - 50px);
  position: relative;
  overflow: visible;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  cursor: crosshair;
  height: ${(props) => props.$height || '400px'};
`;

const SkeletonBox = styled.div`
  width: ${(props) => props.$width || '60px'};
  height: ${(props) => props.$height || '16px'};
  background: ${({ theme }) => theme.border.primary};
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.background.light},
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text.error};
  font-size: 14px;
  gap: 8px;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 16px;
    margin-bottom: 4px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.hide-on-mobile {
    @media ${PHONE_MEDIA_QUERY} {
      display: none;
    }
  }
`;

const StatLabel = styled.span`
  color: ${({ theme }) => theme.text.secondary};
  font-size: 12px;
  font-weight: 500;
`;

const StatValue = styled.span`
  color: ${({ theme }) => theme.text.title};
  font-size: 16px;
  font-weight: 600;

  &.positive {
    color: ${({ theme }) => theme.status.success.primary};
  }

  &.negative {
    color: ${({ theme }) => theme.status.error.primary};
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  display: ${(props) => (props.$visible ? 'block' : 'none')};
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 120px;
  white-space: nowrap;

  .tooltip-time {
    color: ${({ theme }) => theme.text.secondary};
    font-size: 11px;
    margin-bottom: 4px;
  }

  .tooltip-price {
    color: ${({ theme }) => theme.text.title};
    font-size: 14px;
    font-weight: 600;
  }
`;

const TimeframeButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: center;
  flex-direction: row;
`;

const TimeframeButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme }) => theme.border.primary};
  background: ${({ theme, $active }) =>
    $active ? theme.primary.primary : theme.background.primary};
  color: ${({ theme, $active }) =>
    $active ? theme.text.primary : theme.text.secondary};

  @media (hover: hover) {
    &:hover {
      background: ${({ theme, $active }) =>
        $active ? theme.primary.dark : theme.background.light};
      border-color: ${({ theme, $active }) =>
        $active ? theme.primary.dark : theme.border.secondary};
    }
  }

  &:active {
    transform: scale(0.95);
  }
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 10px;
    width: 100%;
    padding: 4px 8px;
  }
`;

// Timeframe options mapping with timeframe, aggregate, and limit parameters
// Note: aggregate values are limited by CoinGecko API:
//   - day: only 1
//   - hour: 1, 4, 12
//   - minute: 1, 5, 15
//   - second: 1, 15, 30
// Use 'limit' to control the number of data points returned (max 1000)
const TIMEFRAME_OPTIONS = [
  { label: '1H', timeframe: 'second', aggregate: 15, limit: 60 * 4 }, // 60 hours
  { label: '1D', timeframe: 'minute', aggregate: 5, limit: 24 * 12 }, // 1 day
  { label: '1W', timeframe: 'hour', aggregate: 1, limit: 7 * 24 }, // 7 days
  { label: '1M', timeframe: 'hour', aggregate: 4, limit: 30 * 6 }, // 30 days
  { label: 'YTD', timeframe: 'day', aggregate: 1, limit: 365 }, // 365 days
  { label: 'ALL', timeframe: 'day', aggregate: 1, limit: 1000 }, // Max: 1000 days
];

const TokenChart = ({ tokenAddress, showStats = true, network = 'solana' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const height = isMobile ? '250px' : '400px';
  const [chartData, setChartData] = useState(null);
  const [canvasRef, setCanvasRef] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });
  const [crosshair, setCrosshair] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const [stats, setStats] = useState(null);
  // Default to '1D' (day timeframe)
  const [selectedOption, setSelectedOption] = useState(
    TIMEFRAME_OPTIONS.find((opt) => opt.label === '1D') || TIMEFRAME_OPTIONS[1],
  );

  // Use RTK Query to fetch OHLCV data
  const {
    data: ohlcvData,
    isLoading,
    isFetching,
    isError,
    error: queryError,
  } = useGetTokenOHLCVQuery(
    {
      network,
      tokenAddress,
      timeframe: selectedOption.timeframe,
      aggregate: selectedOption.aggregate,
      limit: selectedOption.limit,
      currency: 'usd',
    },
    {
      skip: !tokenAddress,
    },
  );

  // Only show loading skeleton on initial load (no data yet)
  const loading = isLoading && !chartData;
  const error = isError
    ? queryError?.data?.message || 'Failed to load chart data'
    : null;

  // Process data when it arrives from RTK Query
  useEffect(() => {
    if (!ohlcvData?.data?.attributes?.ohlcv_list) {
      return;
    }

    const ohlcvList = ohlcvData.data.attributes.ohlcv_list;

    // Transform OHLCV data to chart format
    // OHLCV format: [timestamp, open, high, low, close, volume]
    // Note: CoinGecko returns data in reverse chronological order (newest first)
    // so we reverse it to get oldest to newest for proper chart display
    const items = ohlcvList
      .map(([timestamp, open, high, low, close, volume]) => ({
        unixTime: timestamp,
        value: close, // Use close price for chart
        open,
        high,
        low,
        volume,
      }))
      .reverse(); // Reverse to get chronological order (oldest to newest)

    // Calculate stats
    if (items.length > 0) {
      const firstPrice = items[0].value;
      const lastPrice = items[items.length - 1].value;
      const priceChange = lastPrice - firstPrice;
      const priceChangePercent = ((priceChange / firstPrice) * 100).toFixed(2);
      const high = Math.max(...items.map((item) => item.high));
      const low = Math.min(...items.map((item) => item.low));

      setStats({
        current: lastPrice,
        change: priceChange,
        changePercent: priceChangePercent,
        high,
        low,
      });
    }

    setChartData(items);
  }, [ohlcvData]);

  useEffect(() => {
    if (!canvasRef) return;

    const canvas = canvasRef;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const padding = {
      top: 20,
      right: 20,
      bottom: 30,
      left: isMobile ? 10 : 60,
    };

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // If loading or no data, draw a flat line in the middle
    if (!chartData || chartData.length === 0) {
      const chartWidth = width - padding.left - padding.right;
      const midY = height / 2;

      // Draw a flat line in the middle
      ctx.strokeStyle = theme.border.primary;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(padding.left, midY);
      ctx.lineTo(padding.left + chartWidth, midY);
      ctx.stroke();

      return;
    }

    // Calculate chart dimensions
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Get price range
    const prices = chartData.map((d) => d.value);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;

    // Add some padding to the price range
    const pricePadding = priceRange * 0.1;
    const minY = minPrice - pricePadding;
    const maxY = maxPrice + pricePadding;
    const yRange = maxY - minY;

    // Draw grid lines
    ctx.strokeStyle = theme.border.primary;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    const numGridLines = 5;
    for (let i = 0; i <= numGridLines; i++) {
      const y = padding.top + (chartHeight / numGridLines) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      // Draw price labels using the actual price range (skip on mobile)
      if (!isMobile) {
        const price = maxPrice - (priceRange / numGridLines) * i;
        ctx.fillStyle = theme.text.secondary;
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(formatUSDEnsure2Digits(price), padding.left - 10, y + 4);
      }
    }

    ctx.setLineDash([]);

    // Create gradient for area fill
    const gradient = ctx.createLinearGradient(
      0,
      padding.top,
      0,
      height - padding.bottom,
    );
    const primaryColor = theme.primary.primary;

    // Parse hex color to rgba
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    gradient.addColorStop(0, hexToRgba(primaryColor, 0.3));
    gradient.addColorStop(1, hexToRgba(primaryColor, 0.05));

    // Draw area
    ctx.beginPath();
    chartData.forEach((point, index) => {
      const x = padding.left + (chartWidth / (chartData.length - 1)) * index;
      const y =
        padding.top +
        chartHeight -
        ((point.value - minY) / yRange) * chartHeight;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    // Complete the area
    const lastX = padding.left + chartWidth;
    const firstX = padding.left;
    ctx.lineTo(lastX, height - padding.bottom);
    ctx.lineTo(firstX, height - padding.bottom);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw line
    ctx.beginPath();
    chartData.forEach((point, index) => {
      const x = padding.left + (chartWidth / (chartData.length - 1)) * index;
      const y =
        padding.top +
        chartHeight -
        ((point.value - minY) / yRange) * chartHeight;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw time labels
    const numTimeLabels = 5;
    ctx.fillStyle = theme.text.secondary;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';

    for (let i = 0; i <= numTimeLabels; i++) {
      const index = Math.floor(((chartData.length - 1) / numTimeLabels) * i);
      const point = chartData[index];
      const x = padding.left + (chartWidth / (chartData.length - 1)) * index;
      const date = new Date(point.unixTime * 1000);

      // Format time label based on selected timeframe
      let timeStr;
      switch (selectedOption.label) {
        case '1H':
          // Show minutes for hourly view
          timeStr =
            date.toLocaleTimeString('en-US', {
              minute: '2-digit',
              hour12: false,
            }) + 'm';
          break;
        case '1D':
          // Show hours for daily view
          timeStr =
            date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              hour12: false,
            }) + ':00';
          break;
        case '1W':
          // Show day of week for weekly view
          timeStr = date.toLocaleDateString('en-US', {
            weekday: 'short',
          });
          break;
        case '1M':
          // Show month + day for monthly view
          timeStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          });
          break;
        case 'YTD':
        case 'ALL':
          // Show full date for yearly view
          timeStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit',
          });
          break;
        default:
          timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
      }

      ctx.fillText(timeStr, x, height - padding.bottom + 20);
    }

    // Draw crosshair if visible
    if (crosshair.visible) {
      ctx.strokeStyle = theme.border.primary;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(crosshair.x, padding.top);
      ctx.lineTo(crosshair.x, height - padding.bottom);
      ctx.stroke();

      // Data point dot
      ctx.setLineDash([]);
      ctx.fillStyle = theme.primary.primary;
      ctx.beginPath();
      ctx.arc(crosshair.x, crosshair.y, 4, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [canvasRef, chartData, theme, crosshair, isMobile, selectedOption]);

  const handleMouseMove = (e) => {
    if (!canvasRef || !chartData || chartData.length === 0) return;

    const rect = canvasRef.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = {
      top: 20,
      right: 20,
      bottom: 30,
      left: isMobile ? 10 : 60,
    };
    const chartWidth = rect.width - padding.left - padding.right;

    if (x >= padding.left && x <= rect.width - padding.right) {
      const dataIndex = Math.round(
        ((x - padding.left) / chartWidth) * (chartData.length - 1),
      );
      const point = chartData[dataIndex];

      if (point) {
        // Calculate the Y position of the data point on the chart
        const prices = chartData.map((d) => d.value);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const priceRange = maxPrice - minPrice;
        const pricePadding = priceRange * 0.1;
        const minY = minPrice - pricePadding;
        const maxY = maxPrice + pricePadding;
        const yRange = maxY - minY;
        const chartHeight = rect.height - padding.top - padding.bottom;
        const pointY =
          padding.top +
          chartHeight -
          ((point.value - minY) / yRange) * chartHeight;

        setTooltip({
          visible: true,
          x: x,
          y: y - 10, // Small offset to prevent tooltip from blocking cursor
          data: point,
        });

        setCrosshair({
          visible: true,
          x: x,
          y: pointY,
        });
      }
    } else {
      setTooltip({ ...tooltip, visible: false });
      setCrosshair({ ...crosshair, visible: false });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
    setCrosshair({ ...crosshair, visible: false });
  };

  // Show error state
  if (error) {
    return (
      <ChartContainer $height={height}>
        <ErrorContainer>
          <div>⚠️ {error}</div>
          <div style={{ fontSize: '12px', color: theme.text.secondary }}>
            Please check the token address and try again
          </div>
        </ErrorContainer>
      </ChartContainer>
    );
  }

  // Always render the full UI, with skeleton/placeholder when loading
  return (
    <ChartContainer $height={height}>
      {showStats && (
        <StatsContainer>
          {loading ? (
            <>
              <StatItem>
                <SkeletonBox $width='80px' $height='12px' />
                <SkeletonBox $width='60px' $height='20px' />
              </StatItem>
              <StatItem>
                <SkeletonBox $width='80px' $height='12px' />
                <SkeletonBox $width='60px' $height='20px' />
              </StatItem>
              {!isMobile && (
                <>
                  <StatItem>
                    <SkeletonBox $width='80px' $height='12px' />
                    <SkeletonBox $width='60px' $height='20px' />
                  </StatItem>
                  <StatItem>
                    <SkeletonBox $width='80px' $height='12px' />
                    <SkeletonBox $width='60px' $height='20px' />
                  </StatItem>
                </>
              )}
            </>
          ) : stats ? (
            <>
              <StatItem>
                <StatLabel>Current Price</StatLabel>
                <StatValue>{formatUSDEnsure2Digits(stats.current)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Change</StatLabel>
                <StatValue
                  className={stats.change >= 0 ? 'positive' : 'negative'}
                >
                  {stats.change >= 0 ? '+' : ''}
                  {stats.changePercent}%
                </StatValue>
              </StatItem>
              <StatItem className='hide-on-mobile'>
                <StatLabel>High</StatLabel>
                <StatValue>{formatUSDEnsure2Digits(stats.high)}</StatValue>
              </StatItem>
              <StatItem className='hide-on-mobile'>
                <StatLabel>Low</StatLabel>
                <StatValue>{formatUSDEnsure2Digits(stats.low)}</StatValue>
              </StatItem>
            </>
          ) : null}
        </StatsContainer>
      )}

      <ChartWrapper>
        <Canvas
          $height={height}
          ref={setCanvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        {tooltip.visible && tooltip.data && (
          <Tooltip
            $visible={tooltip.visible}
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className='tooltip-time'>
              {new Date(tooltip.data.unixTime * 1000).toLocaleString()}
            </div>
            <div className='tooltip-price'>
              {formatUSDEnsure2Digits(tooltip.data.value)}
            </div>
          </Tooltip>
        )}
      </ChartWrapper>

      <TimeframeButtons>
        {TIMEFRAME_OPTIONS.map((option) => (
          <TimeframeButton
            key={option.label}
            $active={selectedOption.label === option.label}
            onClick={() => {
              setSelectedOption(option);
            }}
          >
            {option.label}
          </TimeframeButton>
        ))}
      </TimeframeButtons>
    </ChartContainer>
  );
};

export default TokenChart;
